import { ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { BookingService, EmployeeService, transformDateTime } from '@beautyapp/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Store } from '@ngxs/store';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { AuthState } from '@beautyapp/auth';
import {
  CalendarTimeInterval,
  ClearAddEmployeeAppointment,
} from '../../appointment.actions';
import { AppointmentState } from '../../appointment.state';
import {
  AddEmpoyeeMobileAppointmentComponent
} from '../../components/add-empoyee-mobile-appointment/add-empoyee-mobile-appointment.component';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import kaLocale from '@fullcalendar/core/locales/ka';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import { format } from 'date-fns';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { DatepickerComponent } from '@beautyapp/form';

@Component({
    selector: 'beautyapp-employee-mobile-day',
    templateUrl: './employee-mobile-day.component.html',
    styleUrls: ['./employee-mobile-day.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      TranslateModule,
      FullCalendarModule,
      NzLayoutModule,
      NzGridModule,
      NzButtonModule,
      NzIconModule,
      DatepickerComponent
    ]
})
export class EmployeeMobileDayComponent implements OnInit {
  private bookingService = inject(BookingService);
  private modalService = inject(NzModalService);
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);

  userId?: any;
  // beaut-365: day-form slot granularity, shared/persisted via AppointmentState.
  timeInterval?: string = '00:15:00';
  calendarOptions: CalendarOptions = {
    plugins: [timeGridPlugin, interactionPlugin],
    initialView: 'timeGridDay',
    locales: [kaLocale],
    locale: 'ka',
    timeZone: 'Asia/Tbilisi',
    editable: true,
    selectable: true,
    nowIndicator: true,
    height: 'auto',
    eventClick: this.handleEventClick.bind(this),
    dateClick: this.handleDateClick.bind(this),
    select: this.handleDateClick.bind(this),
    slotLabelInterval: '01:00',
    slotDuration: this.timeInterval,
    headerToolbar: false,
    slotLabelFormat: [
      {
        hour: '2-digit',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: false,
        hour12: false
      }
    ],
    dayHeaderFormat: {
      day: '2-digit', month: '2-digit', year: 'numeric', omitCommas: true
    },
    allDaySlot: false,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00',
    customButtons: {
      myCustomButton: {
        text: 'custom!',
        click: function() {
          alert('clicked the custom button!');
        }
      }
    },
    eventDragStop: this.handleEventDragStop.bind(this)
  };
  currentDate!: string;
  startTime!: Date;
  events: any;
  eventsModel: any;
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;


  handleEventDragStop(arg: any) {
    console.log(arg);
  }

  ngOnInit(): void {
    this.userId = this.store.selectSnapshot(AuthState.getProfile)?.id || undefined;
    this.route.params.subscribe((route: any) => {
      if (route && route.userId) {
        this.userId = route.userId;
      }
    });
    this.currentDate = format(new Date(), 'yyyy-MM-dd');
    this.getCalendarBooking({ date: format(new Date(), 'yyyy-MM-dd') });

    this.store.select(AuthState.getActiveBranch)
      .subscribe((res: any) => {
        const startTime = `0${res.whStart}`.slice(-2);
        const endTime = `0${res.whEnd}`.slice(-2);
        this.calendarOptions.slotMinTime = `${startTime}:00:00`;
        this.calendarOptions.slotMaxTime = `${endTime}:00:00`;
      });

    // beaut-365: apply the persisted day-form interval (shared across calendars).
    this.store
      .select(AppointmentState.getCalendarInterval)
      .subscribe((res: any) => {
        if (res) {
          this.timeInterval = res;
          this.calendarOptions.slotDuration = res;
        }
      });
  }

  // beaut-365: change the day-form slot granularity and persist it via the store.
  changeTimeInterval(timeInterval: string): void {
    this.store.dispatch(new CalendarTimeInterval(timeInterval));
    this.timeInterval = timeInterval;
    this.calendarOptions.slotDuration = timeInterval;
  }

  nextDate() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.next();
    this.getCalendarBooking({ date: format(new Date(calendarApi.getCurrentData().currentDate),'yyyy-MM-dd' ) });
  }

  prevDate() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.prev();
    this.getCalendarBooking({ date: format(new Date(calendarApi.getCurrentData().currentDate),'yyyy-MM-dd' ) });
  }

  changeDate($event: any) {
    console.log($event);
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.gotoDate(new Date($event));
    this.getCalendarBooking({ date: format(new Date(calendarApi.getCurrentData().currentDate),'yyyy-MM-dd' ) });
    this.calendarComponent.getApi().render();
  }

  currenDate() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.gotoDate(new Date());
    this.currentDate = format(new Date(), 'yyyy-MM-dd');
    this.getCalendarBooking({ date: format(new Date(), 'yyyy-MM-dd') });
  }

  getCalendarBooking(date: any) {

    const params = {
      ...date,
      userId: this.userId,
      date: format(new Date(date.date), 'yyyy-MM-dd')
    };
    this.bookingService.getCalendarEmployeeBookingNew(params)
      .subscribe((res: any[]) => {
        const mapBooking = res.map((m: any) => {
          return {
            id: m.id,
            start: transformDateTime(m.bookingDate, m.startTime),
            end: transformDateTime(m.bookingDate, m.endTime),
            title: `${m.user?.fullName} ${m.user?.mobilePhone}`,
            backgroundColor: m?.services && m?.services.length ? m?.services[0]?.service?.serviceCategory?.color : null,
            icon: m.status === 2 ? 'lock' : false
          };
        });
        const calendarApi = this.calendarComponent.getApi();
        this.calendarOptions.events = mapBooking;
        this.calendarOptions.eventDidMount = (mountArg: any) => {
          if (mountArg.event.extendedProps['icon']) {
            mountArg.el.querySelector('.fc-event-time').classList.add('fas', 'fa-lock');
          }
        };
        calendarApi.render();
      });

  }

  handleEventClick(clickInfo: EventClickArg) {

    const modal = this.modalService.create({
      nzTitle: this.translateService.instant('calendar.PlanVisit'),
      nzContent: AddEmpoyeeMobileAppointmentComponent,
      nzWidth: '1100px',
      nzStyle: { top: '0px' },
      nzBodyStyle: { padding: '0' },
      nzData: {
        bookingId: clickInfo.event.id
      }
    });
    modal.afterClose.subscribe(res => {
      this.store.dispatch(new ClearAddEmployeeAppointment());
      const calendarApi = this.calendarComponent.getApi();
      this.getCalendarBooking({ date: format(new Date(calendarApi.getCurrentData().currentDate), 'yyyy-MM-dd') });
      setTimeout(() => this.calendarComponent.getApi().render(), 0);
    });
  }

  handleDateClick(clickInfo: any) {
    console.log(clickInfo);
    const dateStr = clickInfo.dateStr;
    const modal = this.modalService.create({
      nzTitle: this.translateService.instant('calendar.PlanVisit'),
      nzContent: AddEmpoyeeMobileAppointmentComponent,
      nzWidth: '1100px',
      nzStyle: { top: '0px' },
      nzBodyStyle: { padding: '0' },
      nzData: {
        date: dateStr
      }
    });

    modal.afterClose.subscribe(res => {
      this.store.dispatch(new ClearAddEmployeeAppointment());
      this.getCalendarBooking({ date: format(new Date(dateStr), 'yyyy-MM-dd') });
      setTimeout(() => this.calendarComponent.getApi().render(), 0);
    });
  }

}
